<!-- Sticky header — mirrors `.cp-hdr` block from gigsumo_job_center_v2(16).html
     Inputs are direction-agnostic so the same layout works for candidate
     and job sliders. -->
<header class="jc-v3-cps-hdr" [attr.data-direction]="direction">
  <div class="jc-v3-cps-hdr-top">
    <!-- Avatar — photo if provided, initials fallback -->
    <div class="jc-v3-cps-av"
         [style.background]="(photoUrl && !imgFailed) ? null : avatarBg"
         [style.color]="avatarFg">
      <img *ngIf="photoUrl && !imgFailed" [src]="photoUrl" alt="" class="jc-v3-cps-av-img"
           (error)="onImgError()">
      <span *ngIf="!photoUrl || imgFailed" class="jc-v3-cps-av-init">{{ initials }}</span>
    </div>

    <!-- Name + availability badge + title/refId + meta icons -->
    <div class="jc-v3-cps-hdr-info">
      <div class="jc-v3-cps-hdr-name-row">
        <!-- Full title on hover via the native `title` attribute — same
             mechanism as the meta items (e.g. "10 Months") below. -->
        <span class="jc-v3-cps-hdr-name" [title]="title">{{ title }}</span>
        <span class="jc-v3-cps-hdr-badge" *ngIf="titleBadge"
              [attr.data-tone]="titleBadge.tone"
              [title]="titleBadge.label">
          <span *ngIf="titleBadge.glyph" aria-hidden="true">{{ titleBadge.glyph }}</span>
          <ng-icon *ngIf="!titleBadge.glyph && titleBadge.icon" [name]="titleBadge.icon"
                   class="jc-v3-cps-hdr-badge-ico"></ng-icon>
          {{ titleBadge.label }}
        </span>
      </div>
      <!-- Upgrade marker (Urgent Hire / Hot). Lives in the sticky header so it
           shows on every slider tab. Reuses the app-wide `.jc-v3-urgent-*`
           badge/countdown styling for parity with the detail pages. -->
      <div class="jc-v3-urgent-row" *ngIf="upgradeBadge" [title]="upgradeBadge.countdown || upgradeBadge.label">
        <span class="jc-v3-urgent-badge">
          <ng-icon name="tablerFlame"></ng-icon> {{ upgradeBadge.label }}
        </span>
        <span class="jc-v3-urgent-countdown" *ngIf="upgradeBadge.countdown">{{ upgradeBadge.countdown }}</span>
      </div>
      <div class="jc-v3-cps-hdr-sub" *ngIf="subtitle || refId">
        <span class="jc-v3-cps-hdr-subtitle" *ngIf="subtitle" [title]="subtitle">{{ subtitle }}</span>
        <span class="jc-v3-cps-hdr-sep" *ngIf="subtitle && refId" aria-hidden="true">&middot;</span>
        <span class="jc-v3-cps-hdr-refid"
              *ngIf="refId"
              [title]="refId"
              style="color:#8B5CF6 !important;background:transparent !important;padding:0;border-radius:0;">{{ refId }}</span>
      </div>
      <div class="jc-v3-cps-hdr-meta" *ngIf="meta?.length">
        <a *ngFor="let item of meta"
           class="jc-v3-cps-hdr-meta-item"
           [class.jc-v3-cps-hdr-meta-email]="item.icon === 'tablerMail'"
           [attr.href]="item.href || null"
           [attr.target]="item.href ? '_blank' : null"
           rel="noopener"
           [title]="item.text">
          <ng-icon [name]="item.icon"></ng-icon>
          <span *ngIf="item.icon !== 'tablerMail'">{{ item.text }}</span>
          <span *ngIf="item.icon === 'tablerMail'" style="color: #2563EB !important;">[ {{ item.text }} ]</span>
        </a>
      </div>
    </div>

    <!-- Match badge — mirrors the candidate-card pattern at
         `candidate-card-table-view.component.html:400-418`: `[class.amber]`
         is bound to every coloured element (badge, pct, lbl) so the SCSS
         can use simple `.foo.amber` compound selectors. -->
    <button type="button"
            class="jc-v3-cps-match-ring"
            *ngIf="!hideMatchRing && (isCeipalFreePlan || (matchScore !== null && matchScore !== undefined))"
            [class.amber]="!isCeipalFreePlan && matchScore >= 40 && matchScore < 75"
            [class.red]="!isCeipalFreePlan && matchScore < 40"
            (click)="matchRingClick.emit()"
            [title]="isCeipalFreePlan ? 'Click to see details' : matchScore + '% AI Match — click to see details'">
      <span class="jc-v3-cps-match-pct"
            [class.amber]="!isCeipalFreePlan && matchScore >= 40 && matchScore < 75"
            [class.red]="!isCeipalFreePlan && matchScore < 40">
        {{ isCeipalFreePlan ? '-' : matchScore + '%' }}
      </span>
      <span class="jc-v3-cps-match-label"
            [class.amber]="!isCeipalFreePlan && matchScore >= 40 && matchScore < 75"
            [class.red]="!isCeipalFreePlan && matchScore < 40">
        {{ matchLabel }}
        <svg class="jc-v3-cps-match-arrow" width="7" height="7" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
          <line x1="7" y1="17" x2="17" y2="7"/>
          <polyline points="7 7 17 7 17 17"/>
        </svg>
      </span>
    </button>

    <!-- Close -->
    <button type="button" class="jc-v3-cps-hdr-close" (click)="closeClick.emit()" aria-label="Close">
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
        <line x1="18" y1="6" x2="6" y2="18"/>
        <line x1="6" y1="6" x2="18" y2="18"/>
      </svg>
    </button>
  </div>

  <!-- Action buttons row -->
  <div class="jc-v3-cps-hdr-actions" *ngIf="actions?.length">
    <button *ngFor="let action of actions; trackBy: trackByActionKey"
            type="button"
            class="jc-v3-cps-hdr-btn"
            [attr.data-tone]="action.tone || 'outline'"
            [disabled]="action.disabled"
            [class.jc-v3-btn-downloading]="action.spinner"
            (click)="actionClick.emit(action.key)">
      <!-- Chat uses the speech-bubble glyph (same as the Chat tab) instead
           of the tablerMessage font icon, so the header + tab chat icons match. -->
      <svg *ngIf="action.key === 'chat'" class="jc-v3-cps-hdr-btn-chat-ico"
           xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24"
           fill="none" stroke="currentColor" stroke-width="2">
        <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"></path>
      </svg>
      <ng-icon *ngIf="action.key !== 'chat'" [name]="action.icon"></ng-icon>
      {{ action.label }}
    </button>
  </div>
</header>
